/**
 * Core mahjong tile types (simplified teaching variant).
 *
 * Tile identity is a discriminated union: suit tiles carry `suit` + `rank`,
 * honors (winds + dragons) carry only their name — the type system itself
 * makes "a run of honors" unrepresentable rather than relying on runtime
 * checks against a sentinel suit. Each physical tile additionally carries a
 * unique numeric `id` so renderers and tests can track individual copies.
 *
 * `kindIndex` maps every tile identity onto a dense 0..33 ordinal (suits
 * first, rank-ascending, then honors) — the canonical sort order and the
 * index space the win-checker's count arrays use.
 */

export type Suit = 'man' | 'pin' | 'sou';

export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export type Honor = 'east' | 'south' | 'west' | 'north' | 'white' | 'green' | 'red';

export const SUITS: readonly Suit[] = ['man', 'pin', 'sou'];

export const RANKS: readonly Rank[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const HONORS: readonly Honor[] = ['east', 'south', 'west', 'north', 'white', 'green', 'red'];

export type TileKind =
  | { readonly kind: 'suit'; readonly suit: Suit; readonly rank: Rank }
  | { readonly kind: 'honor'; readonly honor: Honor };

export type Tile = TileKind & { readonly id: number };

export type Seat = 0 | 1 | 2 | 3;

export const SEAT_COUNT = 4;

/** Every seat in turn order — the typed form of `0..SEAT_COUNT-1`, so loops
 *  over seats keep the `Seat` type instead of widening to `number`. */
export const SEATS: readonly Seat[] = [0, 1, 2, 3];

export const HAND_SIZE = 13;
export const COPIES_PER_KIND = 4;
export const SETS_TO_WIN = 4;
export const SET_SIZE = 3;
export const PAIR_SIZE = 2;

export const SUIT_KIND_COUNT: number = SUITS.length * RANKS.length;
export const KIND_COUNT: number = SUIT_KIND_COUNT + HONORS.length;
export const TILE_COUNT: number = KIND_COUNT * COPIES_PER_KIND;

export type MeldType = 'pung' | 'chi' | 'kong';

/**
 * An open (called) or concealed set. Pung/chi melds hold 3 tiles; a kong
 * meld holds 4 — `tiles` carries no length constraint in the type itself, so
 * callers must switch on `type` before assuming a length. `concealed` is
 * `true` only for a self-drawn concealed kong; Pung and Chi are always
 * claimed off a discard (inherently exposed), and an exposed/added kong
 * stays exposed too — see `game/kongTransitions.ts`.
 */
export interface Meld {
  readonly type: MeldType;
  readonly tiles: readonly Tile[];
  readonly concealed: boolean;
}

/** Dense 0..33 ordinal for a tile identity; ignores `id`. */
export function kindIndex(tile: TileKind): number {
  if (tile.kind === 'suit') {
    return SUITS.indexOf(tile.suit) * RANKS.length + (tile.rank - 1);
  }
  return SUIT_KIND_COUNT + HONORS.indexOf(tile.honor);
}

/** String identity ignoring `id` — e.g. `man3`, `east`. */
export function tileKey(tile: TileKind): string {
  return tile.kind === 'suit' ? `${tile.suit}${tile.rank}` : tile.honor;
}

export function isSameKind(a: TileKind, b: TileKind): boolean {
  return kindIndex(a) === kindIndex(b);
}

/** Non-mutating sort: suits (man, pin, sou) rank-ascending, then honors; ties by id. */
export function sortTiles(tiles: readonly Tile[]): Tile[] {
  return [...tiles].sort((a, b) => kindIndex(a) - kindIndex(b) || a.id - b.id);
}

/** Count of physical copies per identity key (see `tileKey`). */
export function countByKey(tiles: readonly Tile[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const tile of tiles) {
    const key = tileKey(tile);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}
